import { useQuery } from "@apollo/client";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import DialogModal from "src/components/Dialog";
import { Spinner } from "src/components/Spinner";
import { GetAsoEbi } from "src/graphql/queries";
import useAppStore from "src/store/utils";
import { cn } from "src/utils";
import { GetAseobi, GetAseobiVariables } from "src/utils/gql_types";
import EmptyState from "../EmptyState";
import { AddNewGroup } from "../Icons";
import CreateGroup from "../modals/CreateGroup";
import GroupCard from "./GroupCard";
import styles from "./categories.module.scss";

function Categories() {
  const [manualModal, setManualModal] = useState<boolean>(false);
  const { event } = useAppStore((state) => state);
  const navigate = useNavigate();
  const { data: asoebi, loading: asoebiLoading } = useQuery<
    GetAseobi,
    GetAseobiVariables
  >(GetAsoEbi, {
    variables: {
      event_id: event.uuid,
    },
    fetchPolicy: "cache-first",
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(<p>{error?.message ?? "An error occurred"}</p>);
      } else {
        navigate("/clear");
      }
    },
  });

  const aseobiData = asoebi?.getAseobi ?? [];
  const hasData = aseobiData.length > 0;

  // Skeleton UI for loading state
  const renderSkeleton = () => (
    <div className="flex w-full flex-col pr-4 sm:flex-row">
      {Array(3)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className="m-2 h-[240px] w-full animate-pulse rounded-lg bg-gray-200 sm:w-[240px]"
          />
        ))}
    </div>
  );

  return (
    <>
      <div className={styles["categories"]}>
        {asoebiLoading && !hasData && renderSkeleton()}

        {asoebiLoading && hasData && (
          <div className="flex w-full items-center justify-center py-2">
            <Spinner />
          </div>
        )}

        {hasData && (
          <div className={styles["categories__main"]}>
            <div>
              <div className={styles["categories__deck"]}>
                {aseobiData?.map((item) => (
                  <GroupCard key={item.id} group={item} />
                ))}
                <DialogModal
                  trigger={
                    <div
                      className="flex h-[250px] w-full flex-col items-center rounded-[26.2px] px-8 pb-4 pt-[19.5px] transition-all duration-300 ease-in-out hover:scale-[1.05] hover:shadow-lg md:w-max"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='25' ry='25' stroke='%23CBCACA' stroke-width='2' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='squar'/%3e%3c/svg%3e")`,
                      }}
                    >
                      <div
                        className={cn(
                          "h-[68.2px] w-[175px] transition-transform duration-300 group-hover:scale-[1.05]",
                          styles["categories__svg-full"]
                        )}
                      >
                        <AddNewGroup />
                      </div>
                      <div className="mt-[25px]">
                        <h4 className="text-center text-lg font-semibold tracking-[0.5px] text-[#919191] transition-colors duration-300 group-hover:text-[#616161]">
                          Add New Group
                        </h4>
                      </div>
                      <div className="mt-[15.7px]">
                        <p className="max-w-[205.2px] text-center text-sm leading-[19px] text-[#898989] transition-colors duration-300 group-hover:text-[#6b6b6b]">
                          Create a new group that contains attires you want
                          guests to wear during the event
                        </p>
                      </div>
                    </div>
                  }
                  open={manualModal}
                  onOpenChange={setManualModal}
                >
                  <CreateGroup setOpen={setManualModal} />
                </DialogModal>
              </div>
            </div>
          </div>
        )}
      </div>

      {!asoebiLoading && !hasData && (
        <div className={styles["MyParts"]}>
          <EmptyState />
        </div>
      )}
    </>
  );
}

export default Categories;
